import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, CheckCircle, MessageSquare, ChevronDown, ChevronUp, Eye, Send, Type } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FeedbackWithProfile } from "@/types/admin";

export const AdminFeedbackManagement = () => {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<FeedbackWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [unresolvedOpen, setUnresolvedOpen] = useState(true);
  const [resolvedOpen, setResolvedOpen] = useState(false);
  const [responseTexts, setResponseTexts] = useState<{ [key: string]: string }>({});
  const [submittingResponse, setSubmittingResponse] = useState<{ [key: string]: boolean }>({});
  const [resolvingFeedback, setResolvingFeedback] = useState<{ [key: string]: boolean }>({});

  const fetchFeedback = async () => {
    try {
      setLoading(true);

      // Fetch feedback
      const { data: feedbackData, error: feedbackError } = await supabase
        .from("feedback")
        .select("*")
        .order("resolved_at", { ascending: true, nullsFirst: true })
        .order("created_at", { ascending: false });

      if (feedbackError) {
        console.error("Error fetching feedback:", feedbackError);
        throw feedbackError;
      }

      // Fetch all profiles to match with feedback
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name");

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
      }

      // Create a map of user profiles
      const profilesMap = new Map();
      if (profilesData) {
        profilesData.forEach((profile) => {
          profilesMap.set(profile.id, profile);
        });
      }

      // Combine feedback with user data
      const feedbackWithProfiles: FeedbackWithProfile[] = (feedbackData || []).map((item) => {
        const profile = profilesMap.get(item.user_id);
        const userName = profile 
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown User'
          : 'Unknown User';

        return {
          ...item,
          user_name: userName
        };
      });

      setFeedback(feedbackWithProfiles);

    } catch (error: any) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, []);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Bug Report":
        return "destructive";
      case "Feature Request":
        return "default";
      case "General":
        return "secondary";
      case "UI/UX":
        return "outline";
      case "Performance":
        return "default";
      default:
        return "secondary";
    }
  };

  const handleResponseSubmit = async (feedbackId: string) => {
    const responseText = responseTexts[feedbackId];
    if (!responseText?.trim()) {
      toast.error("Please enter a response");
      return;
    }

    setSubmittingResponse(prev => ({ ...prev, [feedbackId]: true }));

    try {
      const { error } = await supabase
        .from("feedback")
        .update({
          admin_response: responseText.trim()
        })
        .eq("id", feedbackId);

      if (error) throw error;

      toast.success("Response added successfully");
      setResponseTexts(prev => ({ ...prev, [feedbackId]: "" }));
      fetchFeedback();
    } catch (error) {
      console.error("Error adding response:", error);
      toast.error("Failed to add response");
    } finally {
      setSubmittingResponse(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const handleMarkResolved = async (feedbackId: string) => {
    setResolvingFeedback(prev => ({ ...prev, [feedbackId]: true }));

    try {
      const { error } = await supabase
        .from("feedback")
        .update({
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id
        })
        .eq("id", feedbackId);

      if (error) throw error;

      toast.success("Feedback marked as resolved");
      fetchFeedback();
    } catch (error) {
      console.error("Error resolving feedback:", error);
      toast.error("Failed to resolve feedback");
    } finally {
      setResolvingFeedback(prev => ({ ...prev, [feedbackId]: false }));
    }
  };

  const unresolvedFeedback = feedback.filter(item => !item.resolved_at);
  const resolvedFeedback = feedback.filter(item => item.resolved_at);

  const TitleCell = ({ title, maxWidth = "max-w-[180px]" }: { title: string; maxWidth?: string }) => {
    const isTruncated = title.length > 25; // Rough estimate for when text gets truncated
    
    if (!isTruncated) {
      return <div className={`truncate ${maxWidth}`}>{title}</div>;
    }

    return (
      <div className="flex items-center gap-2">
        <div className={`font-medium font-inter truncate ${maxWidth}`}>{title}</div>
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 flex-shrink-0"
            >
              <Type className="h-3 w-3" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Full Title</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded">
                <p className="font-inter text-gray-900 break-words">
                  {title}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-anyskill-purple" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Unresolved Feedback */}
      <Collapsible open={unresolvedOpen} onOpenChange={setUnresolvedOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-poppins text-2xl flex items-center gap-2">
                    <MessageSquare className="h-6 w-6" />
                    Unresolved Feedback
                    <Badge variant="destructive" className="ml-2">
                      {unresolvedFeedback.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="font-inter">
                    Feedback that requires attention
                  </CardDescription>
                </div>
                {unresolvedOpen ? <ChevronUp /> : <ChevronDown />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              {unresolvedFeedback.length === 0 ? (
                <p className="text-center py-8 text-gray-500 font-inter">
                  No unresolved feedback.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-poppins w-[200px]">Title</TableHead>
                      <TableHead className="font-poppins w-[100px]">Category</TableHead>
                      <TableHead className="font-poppins w-[120px]">User</TableHead>
                      <TableHead className="font-poppins w-[100px]">Date</TableHead>
                      <TableHead className="font-poppins w-[250px]">Message</TableHead>
                      <TableHead className="font-poppins w-[200px]">Response</TableHead>
                      <TableHead className="font-poppins w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {unresolvedFeedback.map((item) => (
                      <TableRow key={item.id} className="h-20">
                        <TableCell>
                          <TitleCell title={item.title} />
                        </TableCell>
                        <TableCell>
                          <Badge variant={getCategoryColor(item.category)} className="text-xs">
                            {item.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-inter text-sm">
                          <div className="truncate">{item.user_name}</div>
                        </TableCell>
                        <TableCell className="font-inter text-sm">
                          {new Date(item.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="font-inter text-sm text-gray-600 truncate flex-1 max-w-[200px]">
                              {item.message}
                            </div>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 flex-shrink-0"
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-3xl">
                                <DialogHeader>
                                  <DialogTitle>{item.title}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <h4 className="font-semibold mb-2">Full Message:</h4>
                                    <p className="text-gray-700 font-inter whitespace-pre-wrap bg-gray-50 p-4 rounded max-h-64 overflow-y-auto">
                                      {item.message}
                                    </p>
                                  </div>
                                  {item.admin_response && (
                                    <div>
                                      <h4 className="font-semibold mb-2">Admin Response:</h4>
                                      <p className="text-gray-700 font-inter whitespace-pre-wrap bg-blue-50 p-4 rounded border-l-4 border-l-blue-500">
                                        {item.admin_response}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Textarea
                            value={responseTexts[item.id] || ""}
                            onChange={(e) => setResponseTexts(prev => ({ ...prev, [item.id]: e.target.value }))}
                            placeholder="Response..."
                            rows={2}
                            className="text-xs w-full"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              onClick={() => handleResponseSubmit(item.id)}
                              disabled={submittingResponse[item.id] || !responseTexts[item.id]?.trim()}
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 h-7 px-2"
                            >
                              {submittingResponse[item.id] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                            </Button>
                            <Button
                              onClick={() => handleMarkResolved(item.id)}
                              disabled={resolvingFeedback[item.id]}
                              size="sm"
                              variant="outline"
                              className="border-green-600 text-green-600 hover:bg-green-50 h-7 px-2"
                            >
                              {resolvingFeedback[item.id] ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <CheckCircle className="h-3 w-3" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Resolved Feedback */}
      <Collapsible open={resolvedOpen} onOpenChange={setResolvedOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-poppins text-2xl flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                    Resolved Feedback
                    <Badge variant="secondary" className="ml-2">
                      {resolvedFeedback.length}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="font-inter">
                    Previously resolved feedback items
                  </CardDescription>
                </div>
                {resolvedOpen ? <ChevronUp /> : <ChevronDown />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              {resolvedFeedback.length === 0 ? (
                <p className="text-center py-8 text-gray-500 font-inter">
                  No resolved feedback yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-poppins">Title</TableHead>
                      <TableHead className="font-poppins">Category</TableHead>
                      <TableHead className="font-poppins">User</TableHead>
                      <TableHead className="font-poppins">Resolved</TableHead>
                      <TableHead className="font-poppins">Message</TableHead>
                      <TableHead className="font-poppins">Response</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resolvedFeedback.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <TitleCell title={item.title} maxWidth="max-w-[200px]" />
                        </TableCell>
                        <TableCell>
                          <Badge variant={getCategoryColor(item.category)}>
                            {item.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-inter">
                          <div className="truncate max-w-[120px]">{item.user_name}</div>
                        </TableCell>
                        <TableCell className="font-inter">
                          {item.resolved_at ? new Date(item.resolved_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="font-inter text-sm text-gray-600 truncate flex-1 max-w-[200px]">
                              {item.message}
                            </div>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-3xl">
                                <DialogHeader>
                                  <DialogTitle>{item.title}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <h4 className="font-semibold mb-2">Full Message:</h4>
                                    <p className="text-gray-700 font-inter whitespace-pre-wrap bg-gray-50 p-4 rounded max-h-64 overflow-y-auto">
                                      {item.message}
                                    </p>
                                  </div>
                                  {item.admin_response && (
                                    <div>
                                      <h4 className="font-semibold mb-2">Admin Response:</h4>
                                      <p className="text-gray-700 font-inter whitespace-pre-wrap bg-blue-50 p-4 rounded border-l-4 border-l-blue-500">
                                        {item.admin_response}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-inter text-sm text-gray-600 truncate max-w-[200px]">
                            {item.admin_response || 'No response'}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};
